// src/components/homepage/HomePageShowcase.tsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Users, Bot } from 'lucide-react';

import { TeamCard } from '../../../shared/TeamCard';
import { TeamData } from '@dopomogai/types';
import { AgentCard } from '@dopomogai/ui';
import {Button} from '@dopomogai/ui';

// Data Loaders
import { loadTeamPageDataBySlug } from '@dopomogai/data';
import { loadAgentScenario } from '@dopomogai/data';
import { AgentData_V10 } from '@dopomogai/types';

interface ShowcaseDataFromJSON {
  headline: string;
  teamsHeadline: string;
  teamsIntro: string;
  teamsCta: string;
  agentsHeadline: string;
  agentsIntro: string;
  agentsCta: string;
  featuredTeam1Slug: string;
  featuredTeam2Slug: string;
  featuredTeam3Slug: string;
  featuredAgent1Slug: string;
  featuredAgent2Slug: string;
  featuredAgent3Slug: string;
}

interface HomePageShowcaseProps {
  showcaseData: ShowcaseDataFromJSON;
  onAction: (actionType: string, payload?: any) => void;
  id?: string;
}

// Skeleton components for loading state
const CardSkeleton: React.FC = () => (
  <div className="bg-white p-4 rounded-xl border-2 border-black animate-pulse h-full">
    <div className="w-full aspect-[4/3] bg-slate-200 rounded-md mb-4"></div>
    <div className="h-5 w-3/4 bg-slate-200 rounded mb-2"></div>
    <div className="h-4 w-full bg-slate-200 rounded"></div>
  </div>
);

export const HomePageShowcase: React.FC<HomePageShowcaseProps> = ({ showcaseData, onAction, id }) => {
  const { t } = useTranslation('teams_showcase'); 
  
  // State to hold the fully loaded data
  const [featuredTeams, setFeaturedTeams] = useState<(TeamData | null)[]>([]);
  const [featuredAgents, setFeaturedAgents] = useState<(AgentData_V10 | null)[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const teamSlugs = [showcaseData.featuredTeam1Slug, showcaseData.featuredTeam2Slug, showcaseData.featuredTeam3Slug];
        const agentSlugs = [showcaseData.featuredAgent1Slug, showcaseData.featuredAgent2Slug];

        const teamPromises = teamSlugs.map(slug => loadTeamPageDataBySlug(slug));
        const agentPromises = agentSlugs.map(slug => loadAgentScenario(slug));

        const [teams, agents] = await Promise.all([
          Promise.all(teamPromises),
          Promise.all(agentPromises)
        ]);
        
        setFeaturedTeams(teams);
        setFeaturedAgents(agents);
      } catch (error) {
        console.error("Failed to load showcase data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [showcaseData]);

  const columnHoverProps = {
    y: -6, x: -6,
    boxShadow: '6px 6px 0px 0px rgba(17,24,39,1)',
    transition: { duration: 0.15, ease: 'easeOut' },
  };
  const columnTapProps = {
    y: -1, x: -1,
    boxShadow: '1px 1px 0px 0px rgba(17,24,39,1)',
    transition: { duration: 0.05, ease: 'easeOut' },
  };

  return (
    <section id={id} className="bg-slate-50 py-16 md:py-24">
      <div className="container mx-auto px-4">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="text-3xl md:text-4xl lg:text-5xl font-bold text-center text-gray-900 mb-12 md:mb-16"
        >
          {showcaseData.headline}
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-12 items-start">
          
          {/* Teams Column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, ease: 'easeOut' }}
            whileHover={columnHoverProps} whileTap={columnTapProps} onClick={() => onAction('NAVIGATE_TO_PAGE', { path: '/agents/teams' })}
            className="bg-white p-6 md:p-8 rounded-xl border-2 border-black space-y-6 flex flex-col h-full cursor-pointer"
          >
            <div className="flex items-center gap-4"><Users size={32} className="text-red-500"/><h3 className="text-2xl font-bold text-gray-900">{showcaseData.teamsHeadline}</h3></div>
            <p className="text-slate-600 flex-grow">{showcaseData.teamsIntro}</p>
            <div className="flex-shrink-0 grid grid-cols-1 gap-6">
              {isLoading 
                ? Array.from({ length: 3 }).map((_, i) => <CardSkeleton key={i} />)
                : featuredTeams.map((team, index) => team ? <TeamCard key={team.id || index} team={team} onAction={onAction} /> : <CardSkeleton key={index} />)
              }
            </div>
            <div className="mt-auto pt-6 border-t-2 border-slate-100">
              <Button text={showcaseData.teamsCta} variant="primary" fullWidth icon={<ArrowRight size={16}/>} iconPosition="right" onClick={(e) => { e.stopPropagation(); onAction('NAVIGATE_TO_PAGE', { path: '/agents/teams' }); }} className="bg-red-500 hover:bg-red-600"/>
            </div>
          </motion.div>

          {/* Agents Column */}
          <motion.div
            initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, ease: 'easeOut', delay: 0.15 }}
            whileHover={columnHoverProps} whileTap={columnTapProps} onClick={() => onAction('NAVIGATE_TO_PAGE', { path: '/agents' })}
            className="bg-white p-6 md:p-8 rounded-xl border-2 border-black space-y-6 flex flex-col h-full cursor-pointer"
          >
            <div className="flex items-center gap-4"><Bot size={32} className="text-indigo-500"/><h3 className="text-2xl font-bold text-gray-900">{showcaseData.agentsHeadline}</h3></div>
            <p className="text-slate-600 flex-grow">{showcaseData.agentsIntro}</p>
            <div className="flex-shrink-0 grid grid-cols-1 gap-6">
              {isLoading
                ? Array.from({ length: 3 }).map((_, i) => <CardSkeleton key={i} />)
                : featuredAgents.map((agent, index) => agent ? <AgentCard key={agent.id || index} data={{ id: agent.id, slug: agent.id, name: agent.name, tagline: agent.tagline, visual: agent.heroVisual, creatorName: agent.creatorName }} onAction={onAction} /> : <CardSkeleton key={index} />)
              }
            </div>
            <div className="mt-auto pt-6 border-t-2 border-slate-100">
              <Button text={showcaseData.agentsCta} variant="primary" fullWidth icon={<ArrowRight size={16}/>} iconPosition="right" onClick={(e) => { e.stopPropagation(); onAction('NAVIGATE_TO_PAGE', { path: '/agents' }); }} className="bg-indigo-500 hover:bg-indigo-600"/>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
